/* eslint-disable @next/next/no-img-element */
import { VariantProps, cva } from "cva";
import { BuildHTML } from "~/components/BuildHtml/BuildHtml";
import {
  statDescriptionStyles,
  statValueStyles,
} from "~/landing-pages/components/Stats/shared-stat-styles";
import { LandingPageStat } from "~/landing-pages/landing-page-types";
import { tagToClassNameMap } from "~/landing-pages/utils/tagToClassNameMap";
import { Body, Headline } from "~/ui-library";

const imageStyles = cva(
  [
    "h-56 w-full bg-gray-100 object-cover lg:absolute lg:inset-y-0 lg:h-full lg:w-1/2",
  ],
  {
    variants: {
      imageOrientation: {
        start: "order-first lg:left-0",
        end: "order-first lg:right-0",
      },
    },
  }
);

const containerStyles = cva("relative", {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      light: "bg-white",
      dark: "bg-gray-900",
      brand: "bg-brand-indigo",
    },
  },
});

const eyebrowStyles = cva("font-semibold", {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      light: "text-brand-indigo",
      dark: "text-brand-indigo-200",
      brand: "text-brand-indigo-100",
    },
  },
});

const titleStyles = cva("mt-2 font-semibold", {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      light: "text-gray-900",
      dark: "text-white",
      brand: "text-white",
    },
  },
});

const descriptionStyles = cva("mt-6", {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      light: "text-gray-900",
      dark: "text-gray-200",
      brand: "text-gray-200",
    },
  },
});

const contentContainerStyles = cva(
  "px-6 pb-24 pt-16 sm:pb-32 sm:pt-20 lg:px-8 lg:pt-32",
  {
    defaultVariants: {
      imageOrientation: "start",
    },
    variants: {
      imageOrientation: {
        start: "lg:col-start-2",
        end: "lg:col-start-1",
      },
    },
  }
);

type Props = {
  imageAlt: string;
  imageUrl: string;
  eyebrow: string;
  title: string;
  description: string;
  stats: LandingPageStat[];
} & VariantProps<typeof imageStyles> &
  VariantProps<typeof containerStyles>;

export const StatsSplitWithImage: React.FC<Props> = ({
  appearance,
  description,
  eyebrow,
  imageAlt,
  imageOrientation,
  imageUrl,
  stats,
  title,
}) => (
  <div className={containerStyles({ appearance })}>
    <img
      className={imageStyles({ imageOrientation })}
      src={imageUrl}
      alt={imageAlt}
    />
    <div className="mx-auto grid max-w-7xl lg:grid-cols-2">
      <div className={contentContainerStyles({ imageOrientation })}>
        <div className="max-w-2xl lg:mx-l  lg:mr-0 lg:max-w-lg">
          <Body className={eyebrowStyles({ appearance })}>{eyebrow}</Body>
          <Headline
            level={2}
            mdLevel={1}
            className={titleStyles({ appearance })}
          >
            {title}
          </Headline>
          <Body size="large" className={descriptionStyles({ appearance })}>
            <BuildHTML
              html={description}
              tagToClassNameMap={tagToClassNameMap}
            />
          </Body>
          <dl className="mt-12 grid max-w-xl grid-cols-1 gap-8 md:mt-20 md:grid-cols-2 xl:mt-16">
            {stats.map((stat) => (
              <div
                key={`${stat.name}-${stat.value}`}
                className="flex flex-col gap-y-1 border-l-0 border-gray-900/10 md:gap-y-3 md:border-l-1 md:pl-6"
              >
                <Body as="dd" className={statDescriptionStyles({ appearance })}>
                  {stat.name}
                </Body>
                <Headline
                  as="dt"
                  level={3}
                  mdLevel={2}
                  className={statValueStyles({ appearance })}
                >
                  {stat.value}
                </Headline>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  </div>
);
